import { useState, useRef, useCallback, useEffect } from 'react';
import { useSongs } from '@/context/SongContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Play, Square, Check, Loader2, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';

const TIME_SIGNATURES = [
    { id: '4/4', beats: 4, label: '4/4 (Common)' },
    { id: '3/4', beats: 3, label: '3/4 (Waltz)' },
    { id: '2/4', beats: 2, label: '2/4 (March)' },
    { id: '6/8', beats: 6, label: '6/8 (Compound)' },
    { id: '5/4', beats: 5, label: '5/4' },
    { id: '7/8', beats: 7, label: '7/8' },
    { id: '9/8', beats: 9, label: '9/8' },
    { id: '12/8', beats: 12, label: '12/8 (Blues)' },
];

export default function Metronome({ songId, onComplete }) {
    const { addFragment } = useSongs();
    const [bpm, setBpm] = useState(120);
    const [isPlaying, setIsPlaying] = useState(false);
    const [accentFirst, setAccentFirst] = useState(true);
    const [timeSignature, setTimeSignature] = useState('4/4');
    const [beatCount, setBeatCount] = useState(0);
    const [taps, setTaps] = useState([]);
    const [saving, setSaving] = useState(false);
    const [animateBeat, setAnimateBeat] = useState(false);
    
    const audioContextRef = useRef(null);
    const intervalRef = useRef(null);
    const nextNoteTimeRef = useRef(0);
    const beatCountRef = useRef(0);

    const currentTimeSig = TIME_SIGNATURES.find(t => t.id === timeSignature) || TIME_SIGNATURES[0];

    useEffect(() => {
        return () => {
            stopMetronome();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const createClick = useCallback((isAccent) => {
        if (!audioContextRef.current) return;
        const ctx = audioContextRef.current;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        
        osc.connect(gain);
        gain.connect(ctx.destination);
        
        osc.frequency.value = isAccent ? 1000 : 800;
        osc.type = 'sine';
        
        const now = ctx.currentTime;
        gain.gain.setValueAtTime(isAccent ? 0.3 : 0.2, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
        
        osc.start(now);
        osc.stop(now + 0.05);
    }, []);

    const scheduler = useCallback(() => {
        if (!audioContextRef.current) return;
        
        const beats = currentTimeSig.beats;
        
        while (nextNoteTimeRef.current < audioContextRef.current.currentTime + 0.1) {
            const isAccent = accentFirst && beatCountRef.current % beats === 0;
            createClick(isAccent);
            
            setAnimateBeat(true);
            setTimeout(() => setAnimateBeat(false), 100);
            
            beatCountRef.current = beatCountRef.current + 1;
            setBeatCount(beatCountRef.current);
            nextNoteTimeRef.current += 60.0 / bpm;
        }
    }, [bpm, accentFirst, createClick, currentTimeSig.beats]);

    const startMetronome = useCallback(() => {
        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        
        if (audioContextRef.current.state === 'suspended') {
            audioContextRef.current.resume();
        }
        
        beatCountRef.current = 0;
        setBeatCount(0);
        nextNoteTimeRef.current = audioContextRef.current.currentTime;
        intervalRef.current = setInterval(scheduler, 25);
        setIsPlaying(true);
    }, [scheduler]);

    const stopMetronome = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        setIsPlaying(false);
        setBeatCount(0);
        beatCountRef.current = 0;
    }, []);

    // Restart scheduler when bpm or time signature changes while playing
    useEffect(() => {
        if (isPlaying) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            intervalRef.current = setInterval(scheduler, 25);
        }
    }, [bpm, scheduler, isPlaying, timeSignature]);

    const handleTap = () => {
        const now = Date.now();
        const newTaps = [...taps, now].slice(-5);
        setTaps(newTaps);
        
        if (newTaps.length >= 2) {
            const intervals = [];
            for (let i = 1; i < newTaps.length; i++) {
                intervals.push(newTaps[i] - newTaps[i - 1]);
            }
            const avg = intervals.reduce((a, b) => a + b, 0) / intervals.length;
            const tapBpm = Math.round(60000 / avg);
            if (tapBpm >= 40 && tapBpm <= 240) {
                setBpm(tapBpm);
            }
        }
    };

    const adjustBpm = (delta) => {
        setBpm(prev => Math.max(40, Math.min(240, prev + delta)));
    };

    const handleSave = async () => {
        stopMetronome();
        setSaving(true);
        try {
            const fragment = await addFragment(songId, {
                type: 'metadata',
                label: 'Tempo',
                bpm: bpm,
                timeSignature: timeSignature
            });
            toast.success('Tempo saved');
            onComplete(fragment?.id);
        } catch (error) {
            toast.error('Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-5 max-w-sm mx-auto">
            {/* BPM Display */}
            <div className="text-center">
                <div className="flex items-center justify-center gap-3">
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => adjustBpm(-1)}
                        className="h-8 w-8 rounded-full"
                    >
                        <Minus className="w-3 h-3" />
                    </Button>
                    <div className={`transition-transform duration-100 ${animateBeat ? 'scale-105' : ''}`}>
                        <Input
                            type="number"
                            value={bpm}
                            onChange={(e) => setBpm(Math.max(40, Math.min(240, parseInt(e.target.value) || 120)))}
                            className="w-24 h-14 text-center text-3xl font-medium border-0 bg-transparent"
                        />
                    </div>
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => adjustBpm(1)}
                        className="h-8 w-8 rounded-full"
                    >
                        <Plus className="w-3 h-3" />
                    </Button>
                </div>
                <span className="text-sm text-muted-foreground">BPM</span>
            </div>

            {/* Time Signature */}
            <div className="space-y-1.5">
                <Label className="text-xs font-medium">Time Signature</Label>
                <Select value={timeSignature} onValueChange={setTimeSignature}>
                    <SelectTrigger className="h-9">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {TIME_SIGNATURES.map((sig) => (
                            <SelectItem key={sig.id} value={sig.id}>
                                {sig.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Beat indicator */}
            <div className="flex justify-center gap-1.5 flex-wrap max-w-[280px] mx-auto">
                {Array.from({ length: currentTimeSig.beats }, (_, i) => (
                    <div
                        key={i}
                        className={`w-3 h-3 rounded-full transition-all duration-100 ${
                            isPlaying && beatCount % currentTimeSig.beats === i
                                ? i === 0 && accentFirst
                                    ? 'bg-primary scale-125'
                                    : 'bg-muted-foreground scale-110'
                                : 'bg-muted'
                        }`}
                    />
                ))}
            </div>

            {/* Controls */}
            <div className="flex justify-center gap-3">
                <Button
                    onClick={isPlaying ? stopMetronome : startMetronome}
                    variant={isPlaying ? 'secondary' : 'default'}
                    className="w-28 h-10"
                >
                    {isPlaying ? (
                        <><Square className="w-4 h-4 mr-2" /> Stop</>
                    ) : (
                        <><Play className="w-4 h-4 mr-2" /> Start</>
                    )}
                </Button>
                <Button
                    variant="outline"
                    onClick={handleTap}
                    className="w-20 h-10"
                >
                    Tap
                </Button>
            </div>

            {/* Accent toggle */}
            <div className="flex items-center justify-between px-2">
                <Label htmlFor="accent" className="text-sm">Accent beat 1</Label>
                <Switch
                    id="accent"
                    checked={accentFirst}
                    onCheckedChange={setAccentFirst}
                />
            </div>

            {/* Save */}
            <Button
                onClick={handleSave}
                disabled={saving}
                variant="outline"
                className="w-full h-9"
            >
                {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                    <Check className="w-4 h-4 mr-2" />
                )}
                Save as Fragment
            </Button>
        </div>
    );
}
